'use client'

import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassCard } from './components/ui/GlassCard'
import { ChemistContactForm } from './components/ChemistContactForm'
import { Beaker, Coffee, Heart, Droplets, Wind, Pill, CheckCircle, Shield, Clock, FlaskConical, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '@/components/ui/input'

export function FormulationSection() {
  const [expandedFaq, setExpandedFaq] = useState<Set<string>>(new Set())
  const [faqSearchQuery, setFaqSearchQuery] = useState('')
  const [currentFaqPage, setCurrentFaqPage] = useState(1)
  const faqsPerPage = 5
  
  const formulationServices = [
    {
      id: 'beverages',
      title: 'Beverage Formulation',
      description: 'Water-soluble formulations with optimal bioavailability',
      icon: <Coffee className="h-6 w-6" />,
      capabilities: ['Water-soluble', 'Flavor masking', 'Stability testing'],
      color: 'blue'
    },
    {
      id: 'patches',
      title: 'Transdermal Patches',
      description: 'Advanced delivery systems for controlled release',
      icon: <Heart className="h-6 w-6" />,
      capabilities: ['Controlled release', 'Skin penetration', 'Adhesive optimization'],
      color: 'purple'
    },
    {
      id: 'topicals',
      title: 'Topical Formulations',
      description: 'Creams and balms with enhanced absorption',
      icon: <Droplets className="h-6 w-6" />,
      capabilities: ['Enhanced absorption', 'Anti-inflammatory', 'Cosmetic stability'],
      color: 'gold'
    },
    {
      id: 'vapes',
      title: 'Vape Formulations',
      description: 'Harmonizing formulation and application (delivery technologies)',
      icon: <Wind className="h-6 w-6" />,
      capabilities: ['Viscosity optimization', 'Flavor profiles', 'Hardware compatibility'],
      color: 'blue'
    },
    {
      id: 'gummies',
      title: 'Edible Gummies',
      description: 'Consistent dosing and enhanced bioavailability',
      icon: <Pill className="h-6 w-6" />,
      capabilities: ['Precise dosing', 'Texture optimization', 'Shelf-life extension'],
      color: 'purple'
    }
  ]

  const processSteps = [
    {
      icon: <FlaskConical className="h-5 w-5" />,
      title: 'Consultation',
      description: 'Discuss your product requirements and target effects'
    },
    {
      icon: <Beaker className="h-5 w-5" />,
      title: 'Formulation',
      description: 'Develop custom formulas tailored to your needs'
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: 'Testing',
      description: 'Comprehensive stability and safety testing'
    },
    {
      icon: <CheckCircle className="h-5 w-5" />,
      title: 'Production',
      description: 'Scale from R&D batches to full production'
    }
  ]

  // Complete FAQ dataset from the site (from faqs.html)
  const allFaqs = [
    {
      id: 'custom-formulation',
      question: 'What is custom formulation and how does it benefit my product?',
      answer: 'Custom formulation involves developing tailored solutions specific to your product requirements, including optimal dosing, bioavailability, stability, and consumer experience. This ensures your product stands out in the market with superior performance and quality.',
      category: 'Formulation'
    },
    {
      id: 'development-timeline',
      question: 'How long does the formulation development process typically take?',
      answer: 'Formulation timelines vary by complexity, but typically range from 4-12 weeks. Simple reformulations may take 2-4 weeks, while novel delivery systems or complex matrices can take 8-16 weeks including stability testing.',
      category: 'Process'
    },
    {
      id: 'minimum-quantities',
      question: 'What are the minimum quantities for custom formulation projects?',
      answer: 'We work with projects of all sizes. R&D batches can be as small as 100-500 units for initial testing, while production runs typically start at 1,000-5,000 units depending on the product type.',
      category: 'Business'
    },
    {
      id: 'what-is-live-resin',
      question: 'What is Live Resin?',
      answer: 'Live Resin is a type of cannabis concentrate made from fresh, frozen plants to preserve the full spectrum of cannabinoids and terpenes. This process captures the plant\'s natural flavors and effects, resulting in a more potent and aromatic product compared to other extracts.',
      category: 'Products'
    },
    {
      id: 'entourage-effect',
      question: 'What is the Entourage Effect?',
      answer: 'The Entourage Effect refers to how the different compounds in cannabis, like cannabinoids and terpenes, work together to enhance each other\'s benefits. With Live Resin, which preserves the full spectrum of these compounds, the Entourage Effect is maximized, providing more potent and well-rounded effects than using isolated cannabinoids alone.',
      category: 'Science'
    },
    {
      id: 'live-resin-vs-steam-terpenes',
      question: 'Why are Live Resin Derived Terpenes more valuable than Steam-Derived Terpenes?',
      answer: 'Live Resin Terpenes are superior because they are extracted from fresh, frozen plants, preserving the full profile of terpenes and cannabinoids for a more authentic and potent experience. Steam Terpenes, by contrast, are extracted using heat, which can degrade the terpenes and lead to a less flavorful and less effective product. Live Resin Terpenes retain the plant\'s natural characteristics, offering enhanced flavor, aroma, and the full benefits of the Entourage Effect.',
      category: 'Products'
    },
    {
      id: 'what-are-terpenes',
      question: 'What are terpenes and why does preserving them matter?',
      answer: 'Terpenes are natural compounds found in plants, responsible for their aroma, flavor, and some therapeutic effects. In cannabis, terpenes also play a key role in enhancing the overall experience through the Entourage Effect by working together with cannabinoids. Preserving terpenes in products like Live Resin ensures a richer flavor, stronger aroma, and more potent effects, offering a better and more authentic experience compared to products where terpenes are lost during processing.',
      category: 'Science'
    },
    {
      id: 'safety-testing-data',
      question: 'What safety testing data is available?',
      answer: 'We ensure compliance by conducting pre-harvest and post-harvest testing. All of our batches are tested by a third-party certified laboratory to verify potency and terpenes, and check for the presence of pesticides, residual solvents, and heavy metals. This rigorous testing guarantees that our products meet the highest quality and safety standards, giving you confidence in the purity and efficacy of our True Live Resin extracts.',
      category: 'Quality'
    },
    {
      id: 'how-to-formulate',
      question: 'How do I formulate using Live Resin?',
      answer: 'If you want to start using Live Resin, it\'s best to begin by understanding what type of product you need—whether for dabbing, vaping, or incorporating into edibles or topicals. The best path forward is Scheduling a Discovery Call.',
      category: 'Formulation'
    },
    {
      id: 'fake-live-resin',
      question: 'What is Fake Live Resin, and how do I know the difference?',
      answer: 'Fake Live Resin is often mislabeled and made from dried or lower-quality plant material instead of fresh, frozen plants, leading to a less potent and flavorful product. To spot the difference, Genuine Live Resin will have a richer aroma, flavor, and a clear profile of acid-form cannabinoids and full-spectrum terpenes. Checking for lab test results from a trusted source can help ensure the authenticity of the Live Resin.',
      category: 'Quality'
    },
    {
      id: 'cost-of-live-resin',
      question: 'How much does it cost to add Live Resin to my products?',
      answer: 'The cost of adding Live Resin to your products varies by formulation and quantity, but a few common examples... Adding 8% Live Resin HTE to a (1g) cart costs approximately $0.40/cart. Adding 10% Live Resin Concentrate to a (100mg) chocolate costs approximately $0.05/bar. Adding 25% Live Resin HCE to a (2000mg) tincture costs approximately $1.06/bottle. Incorporating Live Resin not only enhances the product\'s quality and appeal but also increases its perceived value to consumers, allowing for a higher sales price and greater marketability. Each case is specific, so it\'s essential to tailor the formulation to your product needs and budget.',
      category: 'Business'
    },
    {
      id: 'what-is-cbda',
      question: 'What is CBDa?',
      answer: 'CBDa, or cannabidiolic acid, is a natural cannabinoid found in raw cannabis and hemp plants. It is the precursor to CBD (cannabidiol) and is primarily found in fresh, unprocessed hemp or cannabis. Virtually all of the cannabinoids in living hemp are found in acid-form. When the plant is exposed to heat through a process known as decarboxylation (like smoking, vaping, or cooking), CBDa converts into CBD. CBDa is gaining attention for its potential health benefits, which are distinct from CBD. Preliminary research suggests that CBDa may have anti-inflammatory, anti-nausea, and even anti-anxiety properties. It\'s thought to work through mechanisms like the serotonin receptor, which helps regulate mood, nausea, and vomiting. Unlike CBD, which has become widely known for its therapeutic uses, CBDa is still a relative newcomer to the market. However, interest in CBDa is growing because some evidence suggests that it might be more potent than CBD in certain areas, particularly in managing nausea and inflammation, and can be absorbed by the body more effectively.',
      category: 'Science'
    },
    {
      id: 'acid-form-cannabinoids',
      question: 'What are acid-form cannabinoids?',
      answer: 'Acid-form cannabinoids, also known as cannabinoid acids, are the naturally occurring, raw versions of cannabinoids found in freshly harvested cannabis and hemp plants. These cannabinoids exist in their acidic form before undergoing decarboxylation, a process that converts them into their more widely known neutral forms, such as CBD and THC. Acid-form cannabinoids have an extra carboxyl group (-COOH) in their chemical structure. This makes them non-psychoactive in their raw state and gives them different pharmacological properties than their decarboxylated counterparts. They convert into their neutral forms (e.g., CBDA to CBD, THCA to THC) when exposed to heat or prolonged storage, which results in the removal of the carboxyl group.',
      category: 'Science'
    },
    {
      id: 'benefits-of-cbda',
      question: 'What are the benefits of CBDa?',
      answer: '1. Anti-Inflammatory Properties - CBDa has shown potential in reducing inflammation by inhibiting the production of certain enzymes and chemicals that trigger inflammatory responses. This may make CBDa useful for conditions like arthritis, inflammatory bowel disease, and other inflammation-related disorders. 2. Anti-Nausea Effects - One of the most well-researched benefits of CBDa is its ability to combat nausea and vomiting. Studies suggest that CBDa interacts with the 5-HT1A serotonin receptor, which plays a key role in regulating nausea and vomiting, making it particularly effective for people undergoing chemotherapy or suffering from motion sickness or pregnancy-related nausea. 3. Anti-Anxiety Potential - CBDa may also have anti-anxiety effects. It is believed to influence serotonin levels in the brain, similar to the way some antidepressant medications work. Early studies have shown that CBDa might be even more effective than CBD in reducing anxiety at lower doses. 4. Anti-Seizure Activity - Like CBD, CBDa has demonstrated anticonvulsant properties in preclinical studies. Though research is still ongoing, this could have implications for its use in managing seizure disorders, including epilepsy. 5. Potential Anti-Cancer Properties - Preliminary studies suggest that CBDa may inhibit the spread of certain types of cancer cells, particularly breast cancer. While much more research is needed, these early findings are promising for future cancer treatments. 6. Pain Relief - Through its anti-inflammatory action and potential interaction with the body\'s pain receptors, CBDa may help in managing pain, particularly in conditions where inflammation plays a major role. 7. Better Bioavailability - CBDa may offer better bioavailability than CBD, meaning it could be more easily absorbed by the body. Some studies suggest that even low doses of CBDa can provide noticeable effects, which could make it a more efficient option for consumers. 8. Higher Dosage - CBDa, partly due to its enhanced bioavailability, can be absorbed by the body in much higher quantities. For conditions that benefit from higher doses, this effectively means it is possible to keep increasing dosage to match the severity of the symptoms. With CBD, the body hits a saturation point much sooner than CBDa, and stops absorbing additional CBD so that no further relief can be gained.',
      category: 'Science'
    },
    {
      id: 'international-shipping',
      question: 'Do you ship internationally?',
      answer: 'Some countries require a prescription/medical exemption. Legal to Ship to: Argentina, Austria, Belgium, Brazil, Bulgaria, Canada, Chile, Colombia, Costa Rica, Croatia, Czechia, Denmark, Estonia, Finland, France, Germany, Greece, Hungary, India, Jamaica, Liechtenstein, Luxembourg, Malta, Mexico, Netherlands, North Macedonia, Norway, Paraguay, Peru, Poland, Portugal, Romania, San Marino, Slovenia, South Korea, Spain, Sweden, Switzerland, Thailand, Ukraine, United Kingdom, Uruguay. Illegal to Ship to: Albania, Andorra, Belarus, Bolivia, China, Ecuador, Egypt, Hong Kong, Iceland, Japan, Lithuania, Malaysia, Moldova, Monaco, Montenegro, Papua New Guinea, Russia, Saudi Arabia, Serbia, Singapore, Slovakia, Turkey, United Arab Emirates, Venezuela.',
      category: 'Business'
    }
  ]

  // Filter FAQs based on search query
  const filteredFaqs = useMemo(() => {
    if (!faqSearchQuery) return allFaqs
    const query = faqSearchQuery.toLowerCase()
    return allFaqs.filter(faq => 
      faq.question.toLowerCase().includes(query) || 
      faq.answer.toLowerCase().includes(query) ||
      faq.category.toLowerCase().includes(query)
    )
  }, [faqSearchQuery])

  // Paginate FAQs
  const totalPages = Math.ceil(filteredFaqs.length / faqsPerPage)
  const paginatedFaqs = useMemo(() => {
    const start = (currentFaqPage - 1) * faqsPerPage
    const end = start + faqsPerPage
    return filteredFaqs.slice(start, end)
  }, [filteredFaqs, currentFaqPage])

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentFaqPage(1)
  }, [faqSearchQuery])

  const toggleFaq = (id: string) => {
    setExpandedFaq(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const expandAllFaqs = () => {
    setExpandedFaq(new Set(paginatedFaqs.map(faq => faq.id)))
  }

  const collapseAllFaqs = () => {
    setExpandedFaq(new Set())
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Aurora Background Effect */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-purple-500/20 to-eskerium-blue/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-gradient-to-l from-eskerium-gold/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-2000" />
      </div>

      <div className="relative w-full h-full p-8 md:px-12 lg:px-16 overflow-y-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Beaker className="h-8 w-8 text-yellow-400" />
              <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Ask a Chemist</span>{' '}

            </h2>
            <p className="text-slate-400 max-w-3xl">
              It's our passion to help brands elevate their products with high quality Live Resin.
              We've been doing this long enough to know nuances of unlocking Live Resin's full potential.
            </p>
          </div>

          {/* Main Content Grid - reorganized per spec */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Contact Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-0">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-eskerium-blue/20 to-eskerium-gold/10 rounded-2xl blur-xl" />
                  <GlassCard 
                    className="relative p-6 border-2 border-purple-500/30 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-transparent backdrop-blur-xl"
                    glowColor="blue"
                  >
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                        <FlaskConical className="h-5 w-5 text-purple-400" />
                        How Can We Help?
                      </h3>
                    </div>
                    <ChemistContactForm 
                      onSubmitSuccess={() => {
                        console.log('Chemist inquiry submitted')
                      }}
                    />
                  </GlassCard>
                </div>
              </div>
            </div>

            {/* Right Column: Process on top, then Expertise + FAQ */}
            <div className="lg:col-span-2 space-y-6">
              {/* Top: We Can Help With - compact cards grid */}
              <GlassCard className="p-6" glowColor="blue">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-eskerium-blue" />
                  We Can Help With…
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {formulationServices.slice(0,5).map((service) => (
                    <GlassCard
                      key={service.id}
                      className="p-4 h-full hover:shadow-elev-1"
                      glowColor={service.color === 'purple' ? 'blue' : (service.color as 'blue' | 'gold')}
                      disableInteractions
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-black/20 text-${service.color === 'gold' ? 'eskerium-gold' : service.color === 'purple' ? 'purple-400' : 'eskerium-blue'}`}>
                          {service.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-white mb-1">{service.title}</h4>
                          <p className="text-xs text-slate-400">{service.description}</p>
                        </div>
                      </div>
                    </GlassCard>
                  ))}
                  {/* Support message filler card */}
                  <GlassCard className="p-4 h-full hover:shadow-elev-1" glowColor="gold" disableInteractions>
                    <div className="h-full flex flex-col justify-center">
                      <h4 className="text-sm font-semibold text-eskerium-gold mb-1">Your Success is Our Success</h4>
                      <p className="text-xs text-slate-300">We are here to help. Please fill out the form on the left and we’ll be in touch quickly.</p>
                    </div>
                  </GlassCard>
                </div>
              </GlassCard>

              {/* Middle: Process Steps */}
              <GlassCard className="p-6" glowColor="blue">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-eskerium-blue" />
                  Our Process
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {processSteps.map((step, index) => (
                    <div key={index} className="text-center">
                      <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-eskerium-blue/10 border border-eskerium-blue/30 flex items-center justify-center text-eskerium-blue">
                        {step.icon}
                      </div>
                      <h4 className="text-sm font-medium text-white mb-1">{step.title}</h4>
                      <p className="text-xs text-slate-400">{step.description}</p>
                    </div>
                  ))}
                </div>
              </GlassCard>

              {/* Bottom: FAQ Card */}
              <GlassCard className="p-6" glowColor="gold">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-eskerium-gold" />
                      Frequently Asked Questions
                    </h3>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search FAQs..."
                        value={faqSearchQuery}
                        onChange={(e) => setFaqSearchQuery(e.target.value)}
                        className="pl-10 bg-gray-800/30 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs text-slate-400">
                        Showing {paginatedFaqs.length} of {filteredFaqs.length} questions
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={expandAllFaqs}
                          className="text-xs text-eskerium-gold hover:text-eskerium-gold/80 transition-colors flex items-center gap-1"
                        >
                          <ChevronDown className="h-3 w-3" />
                          Expand All
                        </button>
                        <button
                          onClick={collapseAllFaqs}
                          className="text-xs text-eskerium-gold hover:text-eskerium-gold/80 transition-colors flex items-center gap-1"
                        >
                          <ChevronUp className="h-3 w-3" />
                          Collapse All
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <AnimatePresence>
                      {paginatedFaqs.length > 0 ? (
                        paginatedFaqs.map((faq) => (
                          <motion.div
                            key={faq.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="border-b border-white/5 last:border-0 pb-3 last:pb-0"
                          >
                            <button
                              onClick={() => toggleFaq(faq.id)}
                              className="w-full text-left flex justify-between items-start gap-2 group"
                            >
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
                                  {faq.question}
                                </h4>
                                <span className="text-xs text-eskerium-gold/60 mt-1">
                                  {faq.category}
                                </span>
                              </div>
                              <span className="text-slate-400 text-xs mt-0.5">
                                {expandedFaq.has(faq.id) ? '−' : '+'}
                              </span>
                            </button>
                            <AnimatePresence>
                              {expandedFaq.has(faq.id) && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="mt-2"
                                >
                                  <p className="text-xs text-slate-400 leading-relaxed">
                                    {faq.answer}
                                  </p>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))
                      ) : (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-sm text-slate-400 text-center py-4"
                        >
                          No FAQs found matching your search.
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex justify-between items-center pt-3 border-t border-white/10">
                      <button
                        onClick={() => setCurrentFaqPage(prev => Math.max(1, prev - 1))}
                        disabled={currentFaqPage === 1}
                        className="flex items-center gap-1 text-xs text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="h-3 w-3" />
                        Previous
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setCurrentFaqPage(page)}
                            className={`w-6 h-6 text-xs rounded ${
                              currentFaqPage === page
                                ? 'bg-eskerium-gold/20 text-eskerium-gold border border-eskerium-gold/30'
                                : 'text-slate-400 hover:text-white hover:bg-white/5'
                            } transition-all`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setCurrentFaqPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentFaqPage === totalPages}
                        className="flex items-center gap-1 text-xs text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </GlassCard>
              </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}